import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  List,
  message,
  Modal,
  Space,
  Statistic,
  Table,
  Tag,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import {
  getVoidedSalesInventoryRestoreCandidates,
  restoreVoidedSaleInventory,
} from "../../../Redux/actions/dailysalesActions";

  const Index = ({ onRestoreComplete }) => {
  const dispatch = useDispatch();
  const csvlinkRef = useRef(null);
  const { user } = useSelector(
    (state) => ({
      user: state.auth.user,
    }),
    shallowEqual
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sales, setSales] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [csvFilename, setCsvFilename] = useState("");
  const [shouldDownloadCsv, setShouldDownloadCsv] = useState(false);
  const [summary, setSummary] = useState({
    totalMatched: 0,
    pendingCount: 0,
    eligibleCount: 0,
    blockedByAuditCount: 0,
    restoredCount: 0,
  });

  const canUseTool = user?.email === "ekcotoco@gmail.com";

  useEffect(() => {
    if (shouldDownloadCsv && csvData.length > 0 && csvlinkRef.current) {
      csvlinkRef.current.link.click();
      setShouldDownloadCsv(false);
    }
  }, [shouldDownloadCsv, csvData, csvlinkRef]);

  const selectedSales = useMemo(() => {
    return sales.filter((item) => selectedRowKeys.includes(item.id));
  }, [sales, selectedRowKeys]);

  const loadCandidates = async () => {
    if (!canUseTool) {
      return;
    }

    setLoading(true);
    try {
      var result = await dispatch(
        getVoidedSalesInventoryRestoreCandidates({
          limit: 500,
        })
      );
      setSales(result?.sales || []);
      setSummary(
        result?.summary || {
          totalMatched: 0,
          pendingCount: 0,
          eligibleCount: 0,
          blockedByAuditCount: 0,
          restoredCount: 0,
        }
      );
      setSelectedRowKeys([]);
    } catch (error) {
      message.error(error?.message || "Failed to load voided sales.");
    }
    setLoading(false);
  };

  const openModal = async () => {
    setIsModalVisible(true);
    await loadCandidates();
  };

  const closeModal = () => {
    if (!loading && !restoreLoading) {
      setIsModalVisible(false);
      setSelectedRowKeys([]);
    }
  };

  const buildRestoreCsvRows = (records, restoredAt) => {
    var rows = [];

    records.forEach((record, saleIndex) => {
      (record.restoreItems || []).forEach((item, itemIndex) => {
        rows.push({
          restore_batch_at: restoredAt,
          restore_order: saleIndex + 1,
          restore_item_order: itemIndex + 1,
          sale_id: record.id,
          branch_id: record.branch,
          branch_name: record.branch_name,
          sale_date: record.sale_date || "",
          voided_at: record.voided_at || "",
          restore_effective_date: record.restore_effective_date || "",
          location_last_audited_at: record.location_last_audited_at || "",
          location_last_audit_source: record.location_last_audit_source || "",
          restored_by: user?.name || "",
          restored_by_email: user?.email || "",
          restore_source: "manual-void-restore",
          inventory_reason: "voided sale",
          inventory_product_id: item.product || "",
          inventory_product_name: item.product_name || "",
          inventory_quantity_restored: item.quantity ?? "",
          inventory_unit: item.unit || "",
        });
      });
    });

    return rows;
  };

  const restoreSales = async (records, exportMode = null) => {
    if (!records?.length) {
      message.warning("No sales selected for restore.");
      return;
    }

    setRestoreLoading(true);
    try {
      var recordsToRestore = [...records]
        .filter(
          (record) =>
            record.void_inventory_restored !== true &&
            record.restore_blocked_by_audit !== true &&
            record.void_inventory_restore_items_count > 0
        )
        .sort((a, b) =>
          (b.restore_effective_date || "").localeCompare(
            a.restore_effective_date || ""
          )
        );

      console.log("restoreSales queued", {
        saleCount: recordsToRestore.length,
        order: recordsToRestore.map((item) => ({
          id: item.id,
          branch: item.branch,
          restore_effective_date: item.restore_effective_date,
        })),
      });

      var restoredRecords = [];
      for (let index = 0; index < recordsToRestore.length; index++) {
        var record = recordsToRestore[index];
        console.log("restoreSales progress", {
          step: `${index + 1}/${recordsToRestore.length}`,
          saleId: record.id,
          branch: record.branch,
          restore_effective_date: record.restore_effective_date,
        });
        var restoreResult = await dispatch(
          restoreVoidedSaleInventory(record, {
            source: "manual-void-restore",
          })
        );
        restoredRecords.push({
          ...record,
          restoreItems: restoreResult?.restoreItems || [],
        });
      }

      message.success(`${recordsToRestore.length} voided sale(s) restored.`);
      if (exportMode === "all_filtered" && restoredRecords.length > 0) {
        var restoredAt = moment().format("YYYY-MM-DD HH:mm:ss");
        setCsvData(buildRestoreCsvRows(restoredRecords, restoredAt));
        setCsvFilename(
          `voided-sale-restore-${moment(restoredAt).format(
            "YYYY-MM-DD-HHmmss"
          )}.csv`
        );
        setShouldDownloadCsv(true);
      }
      await loadCandidates();
      if (onRestoreComplete) {
        onRestoreComplete();
      }
    } catch (error) {
      message.error(error?.message || "Failed to restore inventory.");
    }
    setRestoreLoading(false);
  };

  const columns = [
    {
      title: "Sale",
      key: "sale",
      render: (text, record) => {
        return (
          <div className="vertical">
            <Tag color="red">VOIDED</Tag>
            <span>{record.branch_name}</span>
            <span>{moment(record.restore_effective_date).format("LLL")}</span>
            <span>{record.created_name}</span>
            {record.voided_name ? <span>voided by {record.voided_name}</span> : null}
            {record.void_inventory_restored_at ? (
              <span>
                restored {moment(record.void_inventory_restored_at).format("LLL")}
              </span>
            ) : null}
            {record.location_last_audited_at ? (
              <span>
                last audited {moment(record.location_last_audited_at).format("LLL")}
              </span>
            ) : (
              <span>no audit boundary found</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Products Sold",
      key: "products_sold",
      render: (text, record) => {
        return (
          <List
            size="small"
            dataSource={record.products_sold || []}
            renderItem={(item) => (
              <List.Item>
                <span>{`${item.name} x${item.quantity_sold}`}</span>
              </List.Item>
            )}
          />
        );
      },
    },
    {
      title: "Inventory To Restore",
      key: "restore_items",
      render: (text, record) => {
        if (!record.void_inventory_restore_items_count) {
          return <Tag color="orange">No restore items found</Tag>;
        }

        return (
          <List
            size="small"
            dataSource={record.void_inventory_restore_items || []}
            renderItem={(item) => (
              <List.Item>
                <span>{`${item.product_name} x${item.quantity}`}</span>
              </List.Item>
            )}
          />
        );
      },
    },
    {
      title: "Marker",
      key: "marker",
      render: (text, record) => {
        if (record.void_inventory_restored) {
          return <Tag color="green">Inventory Restored</Tag>;
        }

        if (record.restore_blocked_by_audit) {
          return <Tag color="gold">Blocked By Audit</Tag>;
        }

        if (!record.void_inventory_restore_items_count) {
          return <Tag color="orange">Needs review</Tag>;
        }

        return <Tag color="volcano">Not restored</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        return (
          <Button
            type="primary"
            loading={restoreLoading}
            disabled={
              record.void_inventory_restored === true ||
              record.void_inventory_restore_items_count === 0 ||
              record.restore_blocked_by_audit === true
            }
            onClick={() => {
              restoreSales([record]);
            }}
          >
            restore
          </Button>
        );
      },
    },
  ];

  if (!canUseTool) {
    return null;
  }

  return (
    <>
      <Button onClick={openModal}>voided sale restore tool</Button>
      <div style={{ display: "none" }}>
        <CSVLink
          ref={csvlinkRef}
          data={csvData}
          filename={csvFilename}
          headers={[
            { label: "restore batch at", key: "restore_batch_at" },
            { label: "restore order", key: "restore_order" },
            { label: "restore item order", key: "restore_item_order" },
            { label: "sale id", key: "sale_id" },
            { label: "branch id", key: "branch_id" },
            { label: "branch name", key: "branch_name" },
            { label: "sale date", key: "sale_date" },
            { label: "voided at", key: "voided_at" },
            { label: "restore effective date", key: "restore_effective_date" },
            { label: "location last audited at", key: "location_last_audited_at" },
            { label: "location last audit source", key: "location_last_audit_source" },
            { label: "restored by", key: "restored_by" },
            { label: "restored by email", key: "restored_by_email" },
            { label: "restore source", key: "restore_source" },
            { label: "inventory log reason", key: "inventory_reason" },
            { label: "inventory product id", key: "inventory_product_id" },
            { label: "inventory product name", key: "inventory_product_name" },
            {
              label: "inventory quantity restored",
              key: "inventory_quantity_restored",
            },
            { label: "inventory unit", key: "inventory_unit" },
          ]}
        />
      </div>
      <Modal
        title="Voided Sales Inventory Restore"
        visible={isModalVisible}
        onCancel={closeModal}
        width="88%"
        footer={[
          <Button
            key="refresh"
            onClick={loadCandidates}
            loading={loading}
            icon={<ReloadOutlined />}
          >
            Refresh
          </Button>,
          <Button
            key="restore-selected"
            type="primary"
            loading={restoreLoading}
            disabled={selectedSales.length === 0}
            onClick={() => {
              restoreSales(selectedSales);
            }}
          >
            Restore Selected
          </Button>,
          <Button
            key="restore-all"
            type="primary"
            danger
            loading={restoreLoading}
            disabled={summary.eligibleCount === 0}
            onClick={() => {
              restoreSales(sales, "all_filtered");
            }}
          >
            Restore All Filtered
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Button type="primary" onClick={loadCandidates} loading={loading}>
            Scan Pending Restores
          </Button>

          <div className="horizontalItem" style={{ gap: "1em", flexWrap: "wrap" }}>
            <Statistic title="Matched Voided Sales" value={summary.totalMatched} />
            <Statistic title="Not Yet Restored" value={summary.pendingCount} />
            <Statistic title="Eligible To Restore" value={summary.eligibleCount} />
            <Statistic title="Blocked By Audit" value={summary.blockedByAuditCount} />
            <Statistic title="Already Marked Restored" value={summary.restoredCount} />
          </div>

          <span>
            The list below shows voided sales without a restore marker. Sales older
            than the branch's latest audit boundary are blocked to avoid overwriting
            audited stock. Restores run newest to oldest per your selection.
          </span>

          <Table
            rowKey="id"
            loading={loading}
            dataSource={sales}
            columns={columns}
            pagination={{ pageSize: 10 }}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              getCheckboxProps: (record) => ({
                disabled:
                  record.void_inventory_restore_items_count === 0 ||
                  record.restore_blocked_by_audit === true,
              }),
            }}
          />
        </Space>
      </Modal>
    </>
  );
};

export default Index;
